import React, { useCallback, useRef, useState } from 'react';
import SchemaReferencePanel from './SchemaReferencePanel';

/**
 * BQ-22: Cold-start landing screen. Replaces the old single-card
 * `<FileUpload>` view with a dual-mode hero so the tool's two distinct
 * audiences ("compare model outputs" vs "annotate text") see the relevant
 * affordances first.
 *
 * The schema reference panel below the mode cards is expanded by default
 * for first-time users — once any CTA fires, the parent App writes the
 * onboarded flag to localStorage and the panel collapses on subsequent
 * visits.
 */

const ONBOARDED_KEY = 'ner-comparison-tool/onboarded/v1';

interface LandingPageProps {
  onUploadFile: (file: File, content: string) => void;
  onStartNew: () => void;
  onLoadSample: () => void;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(String(e.target?.result ?? ''));
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
}

const LandingPage: React.FC<LandingPageProps> = ({ onUploadFile, onStartNew, onLoadSample }) => {
  const compareInputRef = useRef<HTMLInputElement>(null);
  const annotateInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  // First-visit users see the schema panel pre-expanded so the JSON shape
  // is one scroll away from the upload buttons.
  const isOnboarded = (() => {
    try {
      return localStorage.getItem(ONBOARDED_KEY) === 'true';
    } catch {
      return false;
    }
  })();

  const handleFile = useCallback(
    async (file: File) => {
      const isJson = file.name.toLowerCase().endsWith('.json') || file.type === 'application/json';
      const isTxt = file.name.toLowerCase().endsWith('.txt') || file.type === 'text/plain';
      if (!isJson && !isTxt) {
        // eslint-disable-next-line no-alert
        alert('Please upload a .json file (or legacy .txt format).');
        return;
      }
      try {
        const content = await readFileAsText(file);
        onUploadFile(file, content);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[LandingPage] file read failed:', e);
        // eslint-disable-next-line no-alert
        alert('Could not read the file. Please try again.');
      }
    },
    [onUploadFile]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    // Clear so the same file can be re-selected.
    e.target.value = '';
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const f = e.dataTransfer.files?.[0];
      if (f) handleFile(f);
    },
    [handleFile]
  );

  return (
    <div className="max-w-5xl mx-auto">
      {/* Hero */}
      <div className="text-center py-8 px-4">
        <h2 className="text-3xl font-bold text-gray-900">NER Comparison &amp; Annotation Tool</h2>
        <p className="mt-2 text-gray-600 max-w-2xl mx-auto">
          Compare named-entity recognition model outputs side by side, or build a labeled gold
          dataset by hand. JSON in, JSON out — no account, your data stays in the browser.
        </p>
      </div>

      {/* Mode cards */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4"
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        {/* Compare mode */}
        <div
          className={
            'border rounded-lg p-6 bg-white shadow-sm transition-colors ' +
            (isDragOver ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300')
          }
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center text-lg font-semibold">
              ⇄
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Compare model outputs</h3>
              <p className="mt-1 text-sm text-gray-600">
                Side-by-side NER models on the same text, score them, find disagreements, export
                annotated comparison.
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => compareInputRef.current?.click()}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Upload model output JSON
            </button>
            <button
              type="button"
              onClick={onLoadSample}
              className="px-4 py-2 text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Try sample data
            </button>
            <input
              ref={compareInputRef}
              type="file"
              accept=".json,application/json,.txt,text/plain"
              onChange={handleInputChange}
              className="hidden"
            />
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Drop a file anywhere on this card to upload. Schema:&nbsp;
            <a href="/data-schema.json" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
              data-schema.json
            </a>
            .
          </p>
        </div>

        {/* Annotate mode */}
        <div className="border rounded-lg p-6 bg-white shadow-sm border-gray-200 hover:border-gray-300 transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg font-semibold">
              ✎
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Annotate text</h3>
              <p className="mt-1 text-sm text-gray-600">
                Build a labeled gold dataset by hand or by correcting model predictions.
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onStartNew}
              className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
            >
              Start a new project
            </button>
            <button
              type="button"
              onClick={() => annotateInputRef.current?.click()}
              className="px-4 py-2 text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Upload existing annotations
            </button>
            <input
              ref={annotateInputRef}
              type="file"
              accept=".json,application/json,.txt,text/plain"
              onChange={handleInputChange}
              className="hidden"
            />
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Annotations save to your browser automatically. Export as JSON when you're done.
          </p>
        </div>
      </div>

      {/* Schema reference */}
      <div className="mt-8 px-4 pb-8">
        <SchemaReferencePanel defaultExpanded={!isOnboarded} />
      </div>
    </div>
  );
};

export { ONBOARDED_KEY };
export default LandingPage;
